import { useCallback, useMemo, useState } from 'react';
import type { NotificationItem, Toast } from '@/data/types';

/** Keep the bell feed bounded — old entries fall off the end. */
const MAX_NOTIFICATIONS = 50;

/**
 * Transient toasts + the persistent notification feed behind the topbar bell.
 *
 * Every `push` does two things: shows the 6-second toast (unchanged), and
 * appends the same event to `notifications` (newest first, session-scoped) so
 * a toast that slipped by is still readable from the bell. `unreadCount`
 * drives the bell's dot; opening the popover calls `markAllRead`.
 */
export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2);
    setToasts(ts => [...ts, { ...t, id }]);
    setNotifications(ns =>
      [
        { id, title: t.title, sub: t.sub, kind: t.kind, ts: Date.now(), read: false },
        ...ns,
      ].slice(0, MAX_NOTIFICATIONS),
    );
    setTimeout(() => setToasts(ts => ts.filter(x => x.id !== id)), 6000);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(ts => ts.filter(x => x.id !== id));
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(ns => (ns.some(n => !n.read) ? ns.map(n => (n.read ? n : { ...n, read: true })) : ns));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  return { toasts, push, dismiss, notifications, unreadCount, markAllRead, clearNotifications };
}
